import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { fmtNum, hubByKey } from "@/lib/hubs";

const PLAN_LABEL = { free: "Starter", rising: "Rising Star", elite: "Elite Label" };
const PLAN_CLS = { free: "bg-white/10 text-zinc-300", rising: "bg-sky-500/15 text-sky-300", elite: "bg-amber-500/15 text-amber-300" };

const Stat = ({ label, value, testId }) => (
  <div className="surface p-5"><div data-testid={testId} className="font-display text-3xl sm:text-4xl font-black">{fmtNum(value)}</div><div className="eyebrow mt-1">{label}</div></div>
);

const Catalog = ({ userId }) => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/admin/users/${userId}/catalog`).then((r) => setC(r.data)); }, [userId]);
  if (!c) return <div className="p-4 text-sm text-zinc-500">Loading…</div>;
  return (
    <div data-testid={`admin-catalog-${userId}`} className="grid md:grid-cols-2 gap-4 p-4 bg-black/20">
      <div>
        <div className="eyebrow mb-2">Tracks · {c.tracks.length}</div>
        {c.tracks.length === 0 && <div className="text-sm text-zinc-500">No tracks.</div>}
        {c.tracks.map((t) => (
          <div key={t.track_id} className="flex items-center gap-2 text-sm py-1 border-b border-white/5">
            <Link to={`/track/${t.track_id}`} className="flex-1 truncate hover:underline">{t.title}</Link>
            <span className="text-[10px] font-mono text-zinc-500 hidden sm:inline">{t.genres.map((g) => hubByKey(g)?.name || g).join(" · ")}</span>
            <span className={`text-[10px] font-mono uppercase ${t.status === "active" ? "text-emerald-400" : "text-red-400"}`}>{t.status}{t.privacy === "private" ? " · private" : ""}</span>
            <span className="font-mono text-xs w-14 text-right">{fmtNum(t.streams)}</span>
          </div>
        ))}
      </div>
      <div>
        <div className="eyebrow mb-2">Releases · {c.albums.length}</div>
        {c.albums.length === 0 && <div className="text-sm text-zinc-500">No releases.</div>}
        {c.albums.map((a) => (
          <div key={a.album_id} className="flex items-center gap-2 text-sm py-1 border-b border-white/5">
            <Link to={`/album/${a.album_id}`} className="flex-1 truncate hover:underline">{a.title}</Link>
            <span className="text-[10px] font-mono uppercase text-zinc-500">{a.type} · {a.track_count} tracks</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [role, setRole] = useState("");
  const [data, setData] = useState({ total: 0, users: [] });
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => {
    const id = setTimeout(() => api.get("/admin/users", { params: { q, plan, role, limit: 200 } }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(id);
  }, [q, plan, role]);
  const sel = "h-11 rounded-md px-3 bg-white/5 border border-white/10 text-sm";
  return (
    <div data-testid="admin-overview" className="mt-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Registered users" value={stats.total_users} testId="admin-stat-users" />
          <Stat label="Artists" value={stats.artists} testId="admin-stat-artists" />
          <Stat label="Listeners" value={stats.listeners} testId="admin-stat-listeners" />
          <Stat label="Active tracks" value={stats.tracks} testId="admin-stat-tracks" />
          <Stat label="Starter" value={stats.plans.free} testId="admin-stat-plan-free" />
          <Stat label="Rising Star" value={stats.plans.rising} testId="admin-stat-plan-rising" />
          <Stat label="Elite Label" value={stats.plans.elite} testId="admin-stat-plan-elite" />
          <Stat label="Releases" value={stats.albums} testId="admin-stat-albums" />
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users by name or email…" className="w-full h-11 pl-10 pr-3 rounded-md bg-white/5 border border-white/10 text-sm outline-none focus:border-[var(--hub)]" />
        </div>
        <select data-testid="admin-plan-filter" value={plan} onChange={(e) => setPlan(e.target.value)} className={sel}>
          <option value="" className="bg-zinc-900">All plans</option>
          {Object.entries(PLAN_LABEL).map(([k, v]) => <option key={k} value={k} className="bg-zinc-900">{v}</option>)}
        </select>
        <select data-testid="admin-role-filter" value={role} onChange={(e) => setRole(e.target.value)} className={sel}>
          <option value="" className="bg-zinc-900">All roles</option>
          {["artist", "listener", "admin"].map((r) => <option key={r} value={r} className="bg-zinc-900 capitalize">{r}</option>)}
        </select>
      </div>
      <div className="text-xs font-mono text-zinc-500 mt-3" data-testid="admin-users-count">{data.total} users</div>
      <div className="surface mt-2 divide-y divide-white/5 overflow-hidden" data-testid="admin-users-table">
        {data.users.map((u) => (
          <div key={u.user_id}>
            <button data-testid={`admin-user-row-${u.user_id}`} onClick={() => setOpen(open === u.user_id ? null : u.user_id)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/[0.03]">
              {open === u.user_id ? <ChevronDown size={14} className="text-zinc-500 shrink-0" /> : <ChevronRight size={14} className="text-zinc-500 shrink-0" />}
              <div className="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 shrink-0 flex items-center justify-center text-sm font-bold">{u.avatar ? <img src={mediaUrl(u.avatar)} alt="" className="w-full h-full object-cover" /> : u.name?.[0]}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{u.name}</div>
                <div className="text-xs text-zinc-500 truncate">{u.email}</div>
              </div>
              <span className="hidden md:inline text-[10px] font-mono uppercase text-zinc-500 w-16">{u.role}</span>
              <span className="hidden md:inline font-mono text-xs text-zinc-400 w-20 text-right">{u.track_count || 0} tracks</span>
              <span data-testid={`admin-user-plan-${u.user_id}`} className={`text-[10px] font-mono uppercase px-2 py-1 rounded-full shrink-0 ${PLAN_CLS[u.plan] || PLAN_CLS.free}`}>{PLAN_LABEL[u.plan] || u.plan}{u.billing_cycle ? ` · ${u.billing_cycle}` : ""}</span>
            </button>
            {open === u.user_id && <Catalog userId={u.user_id} />}
          </div>
        ))}
        {data.users.length === 0 && <div className="p-6 text-sm text-zinc-500">No users match.</div>}
      </div>
    </div>
  );
};
